import React, { useState, useEffect, useRef } from 'react';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import '../styles/global.css';
import { Dropdown } from 'primereact/dropdown';
import { ProgressBar } from 'primereact/progressbar';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import * as L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import * as mapas from '../utils/mapas';

export default function ConsultaCar() {
    const [ufs, setUfs] = useState<any[]>([]);
    const [uf, setUf] = useState<any>(null);
    const [municipios, setMunicipios] = useState<any[]>([]);
    const [municipio, setMunicipio] = useState<any>(null);

    const mapRef = useRef<any>(null);
    const [mapa, setMapa] = useState<L.Map | null>(null);

    const [loading, setLoading] = useState(false);
    const [poligonosCarMap, setPoligonosCarMap] = useState<mapas.AllType>();
    const [cars, setCars] = useState<mapas.CAR[]>([]);
    const [car, setCar] = useState<any>();

    // 1. Carregar os Estados (UFs) via API do IBGE ao montar o componente
    useEffect(() => {
        fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome')
            .then(res => res.json())
            .then(data => setUfs(data))
            .catch(err => console.error('Erro ao buscar UFs:', err));
    }, []);

    // 2. Carregar Municípios quando a UF for selecionada
    useEffect(() => {
        if (uf) {
            setLoading(true);
            fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf.id}/municipios?orderBy=nome`)
                .then(res => res.json())
                .then(data => {
                    setMunicipios(data);
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Erro ao buscar municípios:', err);
                    setLoading(false);
                });
            setMunicipio(null);
        } else {
            setMunicipios([]);
        }
    }, [uf]);

    // 3. Inicializar o Mapa (usando a mesma lógica do CadastroImovel)
    useEffect(() => {
        try {
            if (!mapRef.current) return;
            if (!mapa) {
                const map = mapas.createMapOnBahia(mapRef);
                mapas.refreshingMap(map, setLoading);
                setMapa(map);
            }
        } catch (e: any) {
            console.log('Error ao criar mapa:', e);
        }
    }, [mapRef.current]);

    // Função padrão de seleção de CARs (retirada do CadastroImovel.tsx)
    const selectCar = (carLayer: L.GeoJSON, carsList: Array<any>) => {
        carsList.map((p) => { p.setStyle(mapas.estiloArea); });
        carLayer.setStyle(mapas.estiloSelected);
        carLayer.openPopup();
    };

    // 4. Lidar com a seleção do Município (Buscando o CAR)
    const onMunicipioChange = async (e: any) => {
        const mun = e.value;
        setMunicipio(mun);

        if (mun && mapa) {
            setLoading(true);

            // Remover as camadas anteriores do mapa
            poligonosCarMap?.municipio?.remove();
            try {
                poligonosCarMap?.cars.forEach((p: any) => p.poligono?.remove() || p.remove());
            } catch (error) {
                console.log('Erro ao remover cars anteriores:', error);
            }

            // O utilitário mapas.tsx usa global.uf para buscar os CARs (ex: getCarsEmParalelo)
            if (typeof global !== 'undefined') {
                (global as any).uf = uf.sigla;
            } else {
                (window as any).global = { ...((window as any).global || {}), uf: uf.sigla };
            }

            const psCarMap = await mapas.loadMunicipioCars(
                mapa,
                mun,
                mapas.estiloContorno,
                true,  // flyTo
                true,  // car (Habilita a busca e desenho dos CARs no mapa)
                null,  // navigate (não é estritamente necessário na implementação em paralelo atual do mapas.tsx)
                selectCar,
                setCar,
                setCars
            );

            if (psCarMap) {
                setPoligonosCarMap(psCarMap);
                psCarMap.municipio?.openPopup();
                setTimeout(() => {
                    mapa?.invalidateSize();
                    if (psCarMap.municipio) mapa?.fitBounds(psCarMap.municipio.getBounds());
                }, 100);
            }

            setLoading(false);
        }
    };

    const handleDownloadGeoJSON = () => {
        if (!car || !(car as any).feature) return;
        const feature = (car as any).feature;
        const codigo = feature.properties?.cod_imovel || 'car_selecionado';
        
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(feature));
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute("download", `${codigo}.geojson`);
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    };

    return (
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', height: '100vh', gap: '10px' }}>
            <Card title="Consulta de Imóveis CAR">
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <div className="p-field" style={{ flex: 1 }}>
                        <label htmlFor="uf" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Estado (UF)</label>
                        <Dropdown id="uf" value={uf} options={ufs} onChange={(e) => setUf(e.value)} optionLabel="nome" placeholder="Selecione um Estado" filter style={{ width: '100%' }} />
                    </div>
                    <div className="p-field" style={{ flex: 1 }}>
                        <label htmlFor="municipio" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Município</label>
                        <Dropdown id="municipio" value={municipio} options={municipios} onChange={onMunicipioChange} optionLabel="nome" placeholder="Selecione um Município" filter disabled={!uf || municipios.length === 0} style={{ width: '100%' }} />
                    </div>
                <div className="p-field" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px', marginTop: '24px' }}>
                    <Button label="Baixar Selecionado" icon="pi pi-download" onClick={handleDownloadGeoJSON} disabled={!car} />
                </div>
                </div>
            </Card>
            {loading && <ProgressBar mode="indeterminate" style={{ height: '6px' }} />}
            <div ref={mapRef} style={{ flex: 1, width: '100%', backgroundColor: '#e5e5e5', border: '1px solid #ccc', borderRadius: '4px', minHeight: '500px' }} />
        </div>
    );
}
